import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {APIClient} from '../../../../utils/APIClient';
import {API_URL} from '../../../../settings/local_settings';
import {isOk} from '../../../../utils/apiResponse';
import {Spinner} from '../../../Spinner';
import {NS, checkEmails, displayEmails} from './subscriptionPageModel';
import {BankTransferDetails, LinkButton, Pill} from './SubscriptionParts';
import {recordSubscriptionEvent} from './subscriptionRequests';

const RAW = {escapeValue: false};
const PILL_TONE = {active: 'ok', warning: 'warn', cancelled: 'bad'};

/**
 * «Datos de facturación» (mockup 1). Lo que la agencia resuelve sola (el correo de facturas, D-5) se edita
 * acá, sólo si es admin; lo demás (razón social, forma de pago) se pide por el chat.
 *
 * `canEdit`: `true`/`false` de `is_agency_admin`; `undefined` con el Backend viejo, y ahí no se ofrece
 * «Editar» ni se afirma que haga falta ser admin.
 */
export function BillingDetailsCard({client, methodKey, pill, showPaymentLinkEmail, identity, currencyKey,
                                       bankLines, canEdit, onEmailsSaved, onRequestIdentity, onRequestMethod}) {
    const {t} = useTranslation();
    return (
        <section className="sp-card" aria-labelledby="sp-billing-title">
            <h2 id="sp-billing-title" className="sp-card__title">{t(`${NS}.billing.title`)}</h2>
            <div className="sp-kvs">
                {client && (
                    <EmailRow label={t(`${NS}.billing.invoices_to`)} field="emails_for_invoices"
                              value={client.emails_for_invoices} max={5} canEdit={canEdit === true}
                              onSaved={onEmailsSaved}/>
                )}
                {client && showPaymentLinkEmail && (
                    <EmailRow label={t(`${NS}.billing.payment_link_to`)} field="email_for_payment_link"
                              value={client.email_for_payment_link} max={1} canEdit={canEdit === true}
                              onSaved={onEmailsSaved}/>
                )}
                {client && canEdit === false && (
                    <p className="sp-kvs__note">{t(`${NS}.billing.admin_only`)}</p>
                )}
                {identity && identity.business_name && (
                    <div className="sp-kv">
                        <span className="sp-kv__k">{t(`${NS}.billing.identity`)}</span>
                        <span className="sp-kv__v">
                            {identity.tax_id
                                ? t(`${NS}.billing.identity_value`, {
                                    name: identity.business_name, taxId: identity.tax_id, interpolation: RAW,
                                })
                                : identity.business_name}
                        </span>
                        <LinkButton onClick={onRequestIdentity}>{t(`${NS}.billing.request_change`)}</LinkButton>
                    </div>
                )}
                {methodKey && (
                    <div className="sp-kv">
                        <span className="sp-kv__k">{t(`${NS}.billing.payment_method`)}</span>
                        <span className="sp-kv__v">
                            {t(`${NS}.method.${methodKey}`)}
                            {pill && <> <Pill tone={PILL_TONE[pill]}>{t(`${NS}.plan.sub_${pill}`)}</Pill></>}
                        </span>
                        <LinkButton onClick={onRequestMethod}>{t(`${NS}.billing.change_payment_method`)}</LinkButton>
                    </div>
                )}
                {currencyKey && (
                    <div className="sp-kv">
                        <span className="sp-kv__k">{t(`${NS}.billing.currency`)}</span>
                        <span className="sp-kv__v">{t(`${NS}.billing.${currencyKey}`)}</span>
                        <span/>
                    </div>
                )}
            </div>
            <BankTransferDetails lines={bankLines} titleId="sp-billing-transfer"/>
        </section>
    );
}

/**
 * Un correo editable en línea. Se valida antes de enviar con la misma regla de Tesla; el rechazo del
 * backend se lee por código (`INVALID_EMAIL`), nunca mostrando su `error_message`.
 */
function EmailRow({label, field, value, max, canEdit, onSaved}) {
    const {t} = useTranslation();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const inputId = `sp-email-${field}`;
    const errorId = `${inputId}-error`;
    const hintId = `${inputId}-hint`;
    let describedBy;
    if (error !== null) describedBy = errorId;
    else if (max > 1) describedBy = hintId;

    const start = () => {
        setDraft(displayEmails(value));
        setError(null);
        setEditing(true);
    };

    const invalidText = (emails) => t(`${NS}.billing.emails_invalid`, {emails: emails.join(', '), interpolation: RAW});

    // Al corregir, el rojo se va: el error habla del valor que se intentó guardar, no del que se escribe
    // ahora (QA-spfe-N11).
    const change = (event) => {
        setDraft(event.target.value);
        if (error !== null) setError(null);
    };

    // En el área de texto, Enter guarda como en el campo de una línea; Shift+Enter no hace falta (los
    // correos van separados por coma).
    const enterSaves = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) save(event);
    };

    const save = (event) => {
        event.preventDefault();
        if (saving) return;
        // El correo del link de pago se puede vaciar; el de las facturas, no (contrato §9).
        const check = checkEmails(draft, {max, allowEmpty: max === 1});
        if (!check.ok) {
            setError(check.invalid.length > 0 ? invalidText(check.invalid) : t(`${NS}.billing.emails_hint`));
            return;
        }
        setSaving(true);
        new APIClient(API_URL + 'agency/subscription/billing-emails/')
            .call('PATCH', {[field]: check.value})
            .then((response) => {
                setSaving(false);
                if (isOk(response)) {
                    setEditing(false);
                    recordSubscriptionEvent('billing_email_saved');
                    toast.success(t(`${NS}.billing.emails_saved`));
                    if (onSaved) onSaved(response.data || {[field]: check.value});
                    return;
                }
                // Los rechazos tipados de Tesla (contrato §1.3 y §9), por código: el `invalid` del cuerpo
                // dice cuál; sin él (el campo vacío) o con demasiados, la regla.
                const status = response && response.status;
                const invalid = response && response.raw_body && Array.isArray(response.raw_body.invalid)
                    ? response.raw_body.invalid : [];
                if (status === 'INVALID_EMAIL' && invalid.length > 0) {
                    setError(invalidText(invalid));
                    return;
                }
                if (status === 'INVALID_EMAIL' || status === 'TOO_MANY_EMAILS') {
                    setError(t(`${NS}.billing.emails_hint`));
                    return;
                }
                setError(t(`${NS}.billing.emails_error`));
            });
    };

    if (!editing) {
        return (
            <div className="sp-kv">
                <span className="sp-kv__k">{label}</span>
                <span className="sp-kv__v">{displayEmails(value) || '—'}</span>
                {canEdit ? <LinkButton onClick={start}>{t(`${NS}.billing.edit`)}</LinkButton> : <span/>}
            </div>
        );
    }

    return (
        <form className="sp-kv -editing" onSubmit={save} noValidate>
            <label className="sp-kv__k" htmlFor={inputId}>{label}</label>
            <div className="sp-kv__v">
                {/* Hasta 5 correos no entran en un renglón a 390 px: el de las facturas es un área de texto
                    que crece con lo escrito (QA-VIS-spfe-70). El del link de pago es uno solo. */}
                {max > 1 ? (
                    <textarea id={inputId} className="sp-input -multi" rows={2} value={draft} autoFocus
                              onChange={change} onKeyDown={enterSaves}
                              aria-invalid={error !== null} aria-describedby={describedBy}/>
                ) : (
                    <input id={inputId} className="sp-input" type="text" value={draft} autoFocus
                           onChange={change} aria-invalid={error !== null} aria-describedby={describedBy}/>
                )}
                {error !== null
                    ? <p id={errorId} className="sp-field-error" role="alert">{error}</p>
                    : max > 1 && <p id={hintId} className="sp-field-hint">{t(`${NS}.billing.emails_hint`)}</p>}
            </div>
            <div className="sp-kv__actions">
                {saving ? <Spinner color="positive small"/> : (
                    <>
                        <button type="submit" className="btn -alt">{t(`${NS}.billing.save`)}</button>
                        <button type="button" className="btn -alt -negative" onClick={() => setEditing(false)}>
                            {t(`${NS}.billing.cancel`)}
                        </button>
                    </>
                )}
            </div>
        </form>
    );
}
